import React, { useContext, useMemo, useCallback } from 'react';
import { connect } from 'react-redux';
import { withRouter } from 'react-router';
import { withTranslation } from 'react-i18next';

import { makeGetActiveProjects, makeGetProject } from '../selectors/projects';

import { openFlyout } from '../actions/flyout';
import { showModal } from '../actions/modal';
import { showCreateProjectModal, leaveProject } from '../actions/projects';

import { createAlert } from '../services/alert-service';
import { canCreateProject } from 'services/plan-service';

import { useCanUser } from 'shared-hooks/permission';
import { useFeatureAvailable } from 'shared-hooks/feature';

import { AlertContext } from 'context/AlertContext';

import DropdownBtn from './dropdown-btn';

import IconArrowDown from '../icons/icon-arrow-down';

const ProjectUtils = ({ id, project, projects, btnClassName = 'btn btn-empty', icon, history, location, onDropdownShow, onDropdownHide, t }) => {
    const alertCtx = useContext(AlertContext);

    const timeTrackingAvailable = useFeatureAvailable('timeTracking');
    const hasAutomations = useFeatureAvailable('automations');
    const canViewTimeReports = useCanUser('viewTimeReports');
    const canInviteToProject = useCanUser('inviteToProject', project?.owner);
    const canEditProject = useCanUser('editProject', project?.owner);
    const canCreateProjectTemplate = useCanUser('createProjectTemplate', project?.owner);
    const canLeaveProject = useCanUser('leaveProject', project?.owner);
    const canAccessAutomations = useCanUser('projectAutomations', project?.owner);

    const utils = useMemo(() => {
        const utils = [];

        if (canAccessAutomations && hasAutomations) {
            utils.push({
                text: t('automation:Add Automations'),
                payload: {
                    action: 'automations'
                }
            });
        }

        if (timeTrackingAvailable && canViewTimeReports) {
            utils.push({
                text: t('time-tracking:View time log'),
                payload: {
                    action: 'trackedTime'
                }
            });
        }

        if (canInviteToProject) {
            utils.push({
                text: t('settings:Manage Members'),
                payload: {
                    action: 'invite'
                }
            });
        }

        if (canEditProject) {
            utils.push({
                text: t('Project Settings'),
                payload: {
                    action: 'settings'
                }
            });
        }

        if (canEditProject) {
            utils.push({
                text: t('actions.Duplicate project'),
                payload: {
                    action: 'duplicate'
                }
            });
        }

        if (canCreateProjectTemplate) {
            utils.push({
                text: t('template:save'),
                payload: {
                    action: 'createTemplate'
                }
            });
        }

        if (canEditProject) {
            utils.push({
                text: t('actions.move.title'),
                payload: {
                    action: 'move'
                }
            });

            utils.push({
                text: t('actions.archive.title'),
                payload: {
                    action: 'archive'
                }
            });

            utils.push({
                text: t('actions.delete.title'),
                payload: {
                    action: 'remove'
                }
            });
        }

        if (canLeaveProject) {
            utils.push({
                text: t('actions.leave.title'),
                payload: {
                    action: 'leave'
                },
                prompt: {
                    title: t('actions.leave.title'),
                    text: t('actions.leave.description'),
                    confirmText: t('actions.leave.confirm'),
                    confirmClassName: 'btn-caution'
                }
            });
        }

        return utils;
    }, [timeTrackingAvailable, canViewTimeReports, canInviteToProject, canEditProject, canCreateProjectTemplate, canLeaveProject, canAccessAutomations, hasAutomations, t]);

    const hasOneProject = useMemo(() => projects?.length === 1, [projects?.length])

    const handleClick = useCallback(util => {
        const projectId = id;

        switch (util.payload.action) {
            case 'automations':
                showModal('EDIT_AUTOMATION', { projectId });
                break;

            case 'trackedTime':
                openFlyout('timeTracking', {
                    project: projectId,
                    association: 'project'
                });
                break;

            case 'invite':
                history.push({
                    pathname: `/settings/project/${projectId}/people/invite`,
                    state: {
                        closeUrl: location.pathname
                    }
                });
                break;

            case 'archive':
                if (project.demo && hasOneProject) {
                    createAlert('error', t('alerts.archiveDemo'), {
                        duration: 5000
                    });

                    return;
                }

                showModal('ARCHIVE_PROJECT', {
                    projectId,
                    projectTitle: project.name
                });
                break;

            case 'remove':
                if (project.demo && hasOneProject) {
                    createAlert('error', t('alerts.removeDemo'), {
                        duration: 5000
                    });

                    return;
                }

                showModal('REMOVE_PROJECT', {
                    projectId,
                    projectTitle: project.name,
                    subteam: project.subteam
                });
                break;

            case 'duplicate':
                if (canCreateProject()) {
                    showCreateProjectModal({
                        sourceId: projectId
                    });
                } else {
                    alertCtx.showQuotaAlert('projects');
                }
                break;

            case 'createTemplate':
                showModal('CREATE_TEMPLATE', {
                    resourceId: projectId,
                    type: 'project',
                    name: project.name,
                    disabledOptions: [
                        project.disabled_modules?.includes('milestones') ? 'milestones' : null,
                        project.disabled_modules?.includes('tasks') ? 'task_groups' : null,
                        project.disabled_modules?.includes('tasks') ? 'tasks' : null,
                        project.disabled_modules?.includes('documents') ? 'docs' : null,
                        project.disabled_modules?.includes('files') ? 'files' : null
                    ].filter(Boolean)
                });
                break;

            case 'leave':
                leaveProject(projectId);
                break;

            default:
                history.push({
                    pathname: `/settings/project/${projectId}`,
                    state: {
                        closeUrl: location.pathname
                    }
                });
                break;
        }
    }, [id, project, hasOneProject, history, location, t]);

    return (
        <DropdownBtn
            btnClassName={btnClassName}
            items={utils}
            showArrow={true}
            onDropdownShow={onDropdownShow}
            onDropdownHide={onDropdownHide}
            onItemClick={handleClick}
        >
            { icon || <IconArrowDown /> }
        </DropdownBtn>
    )
}

const makeMapStateToProps = () => {
    const getActiveProjects = makeGetActiveProjects();
    const getProject = makeGetProject();

    return ({ projects }, ownProps) => ({
        projects: getActiveProjects(projects.items),
        project: getProject(projects.items, ownProps.id)
    });
}

export default withRouter(connect(makeMapStateToProps)(withTranslation(['project', 'time-tracking', 'settings', 'automation', 'template'])(ProjectUtils)));
